// Staff-only Help Articles editor rendered inside Settings → Help Articles.
// Edits DB-backed help articles (see backend routers/help_articles.py). The
// editor:
//   • Lists known apps (from GET /help-articles) + lets you type a new slug
//   • Tabs across staff / partner / customer role variants
//   • For each role, edits title, subtitle, and an ordered list of sections
//   • Each section: kicker, title, body_html (textarea), screenshot URL + caption
//   • Live preview of the rendered body underneath the textarea
//
// This is intentionally a "power user" editor — plain HTML in a big textarea,
// no rich text WYSIWYG. Staff paste HTML/URLs, hit Save. If we outgrow it, a
// tiptap editor drops in cleanly.

import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Plus, Trash2, Loader2, Save, ChevronUp, ChevronDown, RotateCcw, ChevronRight, Copy,
} from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription,
  AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { RichTextEditor } from "@/components/RichTextEditor";
import { ScreenshotUploader } from "./ScreenshotUploader";
import { SLUG_PAGES, pageForSlug } from "@/lib/helpSlugs";

const ROLES = ["staff", "partner", "customer"];
const NEW_SECTION = () => ({
  id: `section-${Math.random().toString(36).slice(2, 8)}`,
  kicker: "", title: "New section", body_html: "",
  screenshot_url: "", screenshot_caption: "",
});

// Which sections are expanded, per app + role. Sections start collapsed on
// every app switch; reopening an app you worked on restores what you had open.
const OPEN_KEY = (appSlug, role) => `helpArticles.open.${appSlug}.${role}`;
const readOpen = (appSlug, role) => {
  try {
    const raw = window.localStorage.getItem(OPEN_KEY(appSlug, role));
    return new Set(raw ? JSON.parse(raw) : []);
  } catch { return new Set(); }
};
const writeOpen = (appSlug, role, ids) => {
  try {
    window.localStorage.setItem(OPEN_KEY(appSlug, role), JSON.stringify([...ids]));
  } catch { /* storage disabled — collapse state is best-effort */ }
};

export function HelpArticlesAdmin() {
  const [apps, setApps] = useState([]);              // known slugs
  const [selectedApp, setSelectedApp] = useState("");
  const [newAppSlug, setNewAppSlug] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/help-articles");
        const list = data.apps || [];
        setApps(list);
        setSelectedApp((prev) => prev || list[0] || "");
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load help articles");
      }
    })();
  }, []);

  const addNewApp = () => {
    const slug = newAppSlug.trim().toLowerCase().replace(/[^a-z0-9-]/g, "-");
    if (!slug) return;
    setApps((cur) => cur.includes(slug) ? cur : [...cur, slug].sort());
    setSelectedApp(slug);
    setNewAppSlug("");
  };

  return (
    <Card className="border-slate-200 shadow-sm p-6 space-y-5"
          data-testid="help-articles-admin-panel">
      <div>
        <h3 className="font-heading text-base font-semibold text-slate-800">Help Articles</h3>
        <p className="mt-1 text-sm text-slate-500">
          Edit the &ldquo;How to&rdquo; article each app shows to its users. Articles are
          scoped by role — you can write a completely different version for staff, partners,
          and customers.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <label className="text-xs font-medium text-slate-600">App</label>
        {apps.length > 0 ? (
          <select
            className="rounded-md border border-slate-200 bg-white px-2 py-1.5 text-sm min-w-[200px]"
            value={selectedApp}
            onChange={(e) => setSelectedApp(e.target.value)}
            data-testid="help-articles-app-select"
          >
            {apps.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
        ) : (
          <span className="text-xs text-slate-400">No apps yet — add one on the right.</span>
        )}
        <div className="ml-auto flex items-center gap-2">
          <Input
            placeholder="new-app-slug (e.g. tickets)"
            value={newAppSlug}
            onChange={(e) => setNewAppSlug(e.target.value)}
            className="h-9 w-56 text-sm"
            data-testid="help-articles-new-app-slug"
          />
          <Button onClick={addNewApp} variant="outline"
                  disabled={!newAppSlug.trim()}
                  data-testid="help-articles-add-app-btn">
            <Plus className="h-4 w-4" /> Add app
          </Button>
        </div>
      </div>

      {selectedApp && <AppEditor key={selectedApp} appSlug={selectedApp} />}
    </Card>
  );
}

function AppEditor({ appSlug }) {
  const [role, setRole] = useState("staff");
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50/40 p-3"
         data-testid={`help-articles-editor-${appSlug}`}>
      <div className="text-xs text-slate-500 mb-2">
        Editing article slug <code className="rounded bg-slate-100 px-1.5 py-0.5">{appSlug}</code>
        <span className="ml-2" data-testid="help-articles-slug-page">
          · &ldquo;How to&rdquo; button appears on <code className="rounded bg-slate-100 px-1.5 py-0.5">{pageForSlug(appSlug)}</code>
          {!SLUG_PAGES[appSlug] && " (and its sub-pages) — the slug must match the page address"}
        </span>
      </div>
      <Tabs value={role} onValueChange={setRole}>
        <TabsList className="grid grid-cols-3 max-w-md">
          {ROLES.map((r) => (
            <TabsTrigger key={r} value={r} data-testid={`help-articles-role-tab-${r}`}
                         className="capitalize">
              {r}
            </TabsTrigger>
          ))}
        </TabsList>
        {ROLES.map((r) => (
          <TabsContent key={r} value={r}>
            <RoleArticleForm appSlug={appSlug} role={r} />
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}

function RoleArticleForm({ appSlug, role }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [title, setTitle] = useState("");
  const [subtitle, setSubtitle] = useState("");
  const [sections, setSections] = useState([]);
  const [meta, setMeta] = useState({});
  const [open, setOpen] = useState(() => readOpen(appSlug, role));

  const toggleOpen = (sid) => {
    setOpen((cur) => {
      const next = new Set(cur);
      if (next.has(sid)) next.delete(sid); else next.add(sid);
      writeOpen(appSlug, role, next);
      return next;
    });
  };
  const setAllOpen = (expand) => {
    const next = expand ? new Set(sections.map((s, i) => s.id || `#${i}`)) : new Set();
    writeOpen(appSlug, role, next);
    setOpen(next);
  };

  const load = async () => {
    setLoading(true);
    try {
      // Use the staff-list endpoint to grab this specific (app, role) variant
      // — /for-me returns the caller's own role, not what we're editing.
      const { data } = await api.get(`/help-articles/${appSlug}`);
      const doc = (data.articles || []).find((a) => a.role === role);
      setTitle(doc?.title || "");
      setSubtitle(doc?.subtitle || "");
      setSections((doc?.sections || []).map((s) => ({ ...s })));
      setMeta({ updated_at: doc?.updated_at, updated_by_name: doc?.updated_by_name });
      setDirty(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); setOpen(readOpen(appSlug, role)); }, [appSlug, role]);

  const patchSection = (idx, patch) => {
    setSections((cur) => cur.map((s, i) => (i === idx ? { ...s, ...patch } : s)));
    setDirty(true);
  };
  const moveSection = (idx, dir) => {
    setSections((cur) => {
      const next = [...cur];
      const j = idx + dir;
      if (j < 0 || j >= next.length) return cur;
      [next[idx], next[j]] = [next[j], next[idx]];
      return next;
    });
    setDirty(true);
  };
  const removeSection = (idx) => {
    if (!window.confirm("Remove this section?")) return;
    setSections((cur) => cur.filter((_, i) => i !== idx));
    setDirty(true);
  };
  const addSection = () => {
    const s = NEW_SECTION();
    setSections((cur) => [...cur, s]);
    setOpen((cur) => {
      const next = new Set(cur).add(s.id);
      writeOpen(appSlug, role, next);
      return next;
    });
    setDirty(true);
  };

  const save = async () => {
    // Dedupe id check — server does it too but a friendlier UI message helps.
    const ids = sections.map((s) => s.id).filter(Boolean);
    if (new Set(ids).size !== ids.length) {
      toast.error("Two sections share the same id — make each unique."); return;
    }
    if (!title.trim()) { toast.error("Title is required"); return; }
    setSaving(true);
    try {
      const { data } = await api.put(`/help-articles/${appSlug}/${role}`, {
        title: title.trim(), subtitle: subtitle.trim(), sections,
      });
      toast.success(`Saved ${appSlug} / ${role}`);
      setMeta({ updated_at: data.updated_at, updated_by_name: data.updated_by_name });
      setDirty(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  const resetToDefault = async () => {
    if (!window.confirm(
      `Overwrite the ${role} article for "${appSlug}" with the shipped default? ` +
      `Any customisations you've made will be lost. This cannot be undone.`
    )) return;
    setSaving(true);
    try {
      const { data } = await api.post(`/help-articles/${appSlug}/${role}/reset-to-default`);
      setTitle(data.title || "");
      setSubtitle(data.subtitle || "");
      setSections((data.sections || []).map((s) => ({ ...s })));
      setMeta({ updated_at: data.updated_at, updated_by_name: data.updated_by_name });
      setDirty(false);
      toast.success(`Reset ${appSlug} / ${role} to default`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Reset failed");
    } finally { setSaving(false); }
  };

  if (loading) {
    return <div className="flex items-center gap-2 p-4 text-slate-400 text-sm">
      <Loader2 className="h-4 w-4 animate-spin" /> Loading…
    </div>;
  }

  return (
    <div className="space-y-4 mt-3" data-testid={`help-articles-form-${role}`}>
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1">
          <label className="text-xs font-medium text-slate-600">Article title</label>
          <Input value={title} onChange={(e) => { setTitle(e.target.value); setDirty(true); }}
                 data-testid={`help-articles-title-${role}`}
                 placeholder="e.g. UC AI Outbound — How to" />
        </div>
        <div className="space-y-1">
          <label className="text-xs font-medium text-slate-600">Subtitle</label>
          <Input value={subtitle} onChange={(e) => { setSubtitle(e.target.value); setDirty(true); }}
                 data-testid={`help-articles-subtitle-${role}`}
                 placeholder="e.g. Everything you can do as a staff user." />
        </div>
      </div>

      <div className="space-y-2">
        {sections.length > 1 && (
          <div className="flex items-center justify-end gap-2 text-[11px] text-slate-500">
            <span>{open.size} of {sections.length} expanded</span>
            <button type="button" onClick={() => setAllOpen(true)}
                    data-testid={`help-articles-expand-all-${role}`}
                    className="rounded border border-slate-200 bg-white px-2 py-0.5 font-medium hover:bg-slate-50">Expand all</button>
            <button type="button" onClick={() => setAllOpen(false)}
                    data-testid={`help-articles-collapse-all-${role}`}
                    className="rounded border border-slate-200 bg-white px-2 py-0.5 font-medium hover:bg-slate-50">Collapse all</button>
          </div>
        )}
        {sections.map((s, i) => (
          <SectionEditor
            key={i}
            section={s}
            index={i}
            total={sections.length}
            expanded={open.has(s.id || `#${i}`)}
            onToggle={() => toggleOpen(s.id || `#${i}`)}
            onChange={(patch) => patchSection(i, patch)}
            onMoveUp={() => moveSection(i, -1)}
            onMoveDown={() => moveSection(i, +1)}
            onRemove={() => removeSection(i)}
            role={role}
          />
        ))}
        <Button variant="outline" onClick={addSection}
                data-testid={`help-articles-add-section-${role}`}
                className="w-full border-dashed border-slate-300 text-slate-500 hover:bg-slate-50">
          <Plus className="h-4 w-4" /> Add section
        </Button>
      </div>

      <div className="flex items-center justify-between pt-2 border-t border-slate-100">
        <div className="flex items-center gap-3">
          <div className="text-xs text-slate-400">
            {meta.updated_at
              ? `Last saved ${new Date(meta.updated_at).toLocaleString()}${meta.updated_by_name ? ` · by ${meta.updated_by_name}` : ""}`
              : "Never saved yet."}
            {dirty && <span className="ml-2 rounded-full bg-amber-100 text-amber-800 px-2 py-0.5 text-[10px] font-semibold">Unsaved changes</span>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={resetToDefault}
                  disabled={saving}
                  data-testid={`help-articles-reset-${role}`}
                  className="text-slate-600 hover:text-slate-800"
                  title="Overwrite this variant with the shipped default content">
            <RotateCcw className="h-3.5 w-3.5" />
            Reset to default
          </Button>
          <ReplicateMenu appSlug={appSlug} role={role} disabled={saving || dirty || !meta.updated_at} />
          <Button onClick={save} disabled={saving || !dirty}
                  data-testid={`help-articles-save-${role}`}
                  className="bg-primary hover:bg-primary/90 text-primary-foreground">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Save {role}
          </Button>
        </div>
      </div>
    </div>
  );
}

function ReplicateMenu({ appSlug, role, disabled }) {
  const [target, setTarget] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      await api.post(`/help-articles/${appSlug}/${role}/replicate`, { target_role: target });
      toast.success(`Copied ${role} article to ${target}`);
      setTarget(null);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Copy failed");
    } finally { setBusy(false); }
  };
  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" disabled={disabled} data-testid={`help-articles-replicate-${role}`}
                  title={disabled ? "Save this article first" : "Copy this article to another role"}>
            <Copy className="h-3.5 w-3.5" /> Copy to…
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {ROLES.filter((r) => r !== role).map((r) => (
            <DropdownMenuItem key={r} className="capitalize" onSelect={() => setTarget(r)}
                              data-testid={`help-articles-replicate-${role}-to-${r}`}>
              {r}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
      <AlertDialog open={!!target} onOpenChange={(o) => !o && !busy && setTarget(null)}>
        <AlertDialogContent data-testid="help-articles-replicate-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Replace the {target} article?</AlertDialogTitle>
            <AlertDialogDescription>
              The saved <b>{role}</b> article for <code>{appSlug}</code> will be copied to <b>{target}</b>.
              All existing <b>{target}</b> content — title, subtitle and every section — will be
              replaced. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy} data-testid="help-articles-replicate-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); run(); }} disabled={busy}
                               className="bg-red-600 hover:bg-red-700"
                               data-testid="help-articles-replicate-confirm-btn">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />} Replace {target} content
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function SectionEditor({ section, index, total, onChange, onMoveUp, onMoveDown, onRemove, role,
                        expanded, onToggle }) {
  return (
    <div className="rounded-md border border-slate-200 bg-white p-3 space-y-2"
         data-testid={`help-section-${role}-${index}`} data-expanded={expanded ? "true" : "false"}>
      <div className="flex items-center gap-2">
        <button type="button" onClick={onToggle}
                data-testid={`help-section-toggle-${role}-${index}`}
                title={expanded ? "Collapse section" : "Expand section"}
                className="p-1 text-slate-400 transition-colors hover:text-slate-700">
          <ChevronRight className={`h-4 w-4 transition-transform ${expanded ? "rotate-90" : ""}`} />
        </button>
        <span className="text-[10px] font-mono text-slate-400">#{index + 1}</span>
        {expanded ? (
          <>
            <Input value={section.id}
                   onChange={(e) => onChange({ id: e.target.value.trim().toLowerCase().replace(/[^a-z0-9-]/g, "-") })}
                   className="h-8 text-xs w-40 font-mono"
                   placeholder="section-id (unique)"
                   data-testid={`help-section-id-${role}-${index}`} />
            <Input value={section.title}
                   onChange={(e) => onChange({ title: e.target.value })}
                   className="h-8 text-sm flex-1"
                   placeholder="Section title"
                   data-testid={`help-section-title-${role}-${index}`} />
          </>
        ) : (
          <button type="button" onClick={onToggle}
                  data-testid={`help-section-summary-${role}-${index}`}
                  className="flex min-w-0 flex-1 items-baseline gap-2 text-left">
            <span className="truncate text-sm font-medium text-slate-700">{section.title || "Untitled section"}</span>
            {section.kicker && <span className="truncate text-[11px] text-slate-400">{section.kicker}</span>}
            <span className="ml-auto shrink-0 font-mono text-[10px] text-slate-300">{section.id}</span>
          </button>
        )}
        <div className="flex items-center gap-1 shrink-0">
          <button onClick={onMoveUp} disabled={index === 0}
                  className="p-1 text-slate-400 hover:text-slate-700 disabled:opacity-30"
                  title="Move up"><ChevronUp className="h-4 w-4" /></button>
          <button onClick={onMoveDown} disabled={index === total - 1}
                  className="p-1 text-slate-400 hover:text-slate-700 disabled:opacity-30"
                  title="Move down"><ChevronDown className="h-4 w-4" /></button>
          <button onClick={onRemove}
                  className="p-1 text-slate-400 hover:text-red-600"
                  title="Remove"><Trash2 className="h-4 w-4" /></button>
        </div>
      </div>
      {expanded && (
        <>
          <Input value={section.kicker || ""}
                 onChange={(e) => onChange({ kicker: e.target.value })}
                 placeholder="Kicker (e.g. Getting started)"
                 className="h-8 text-xs"
                 data-testid={`help-section-kicker-${role}-${index}`} />
          <div>
            <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Screenshot</label>
            <ScreenshotUploader
              url={section.screenshot_url || ""}
              onChange={(url) => onChange({ screenshot_url: url })}
              testid={`help-section-screenshot-${role}-${index}`}
            />
          </div>
          {section.screenshot_url && (
            <Input value={section.screenshot_caption || ""}
                   onChange={(e) => onChange({ screenshot_caption: e.target.value })}
                   placeholder="Screenshot caption"
                   className="h-8 text-xs"
                   data-testid={`help-section-caption-${role}-${index}`} />
          )}
          <div>
            <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Body</label>
            <RichTextEditor
              value={section.body_html || ""}
              onChange={(html) => onChange({ body_html: html })}
              placeholder="Write the section body — use the toolbar for headings, lists, links, and inline images."
              testid={`help-section-body-${role}-${index}`}
            />
          </div>
        </>
      )}
    </div>
  );
}
